import { FormGroup, Input } from '@snsw/react-component-library';
import { useOptionallyControlledValue } from 'components/Utils/useOptionallyControlledValue';
import { ChangeEvent, forwardRef } from 'react';
import styled from 'styled-components';

const StyledFormGroup = styled(FormGroup)`
  margin-top: 0;

  // Fix the padding on the error fields so the text aligns with the icon properly
  span[class^='FormError'] {
    padding-top: 2px;
  }
`;

export interface ContactNumberProps {
  /** The id to be used on the input field. */
  id?: string;

  /** The name to be used on the input field. */
  name: string;

  /** Label to show above the field */
  label?: string;

  /** Optional help message to show below the label */
  helpMessage?: string;

  /** Error message for each of the form components */
  errorMessage?: string;

  /** Determines if the relevant `errorMessage` will be shown next to each field */
  hasError?: boolean;

  /** `true` to disable input on the field */
  disabled?: boolean;

  /** Should non-numeric characters be allowed in the 'number' field? Defaults to `false`. */
  allowNonNumeric?: boolean;

  /** Default value to supply for an uncontrolled input */
  defaultValue?: string;

  /** Actual value for controlled input */
  value?: string;

  /** Notified when the prefix or the number changes */
  onChange?: (value: string) => void;

  /** Optional class name to pass to the form group */
  className?: string;
}

export const ContactNumber = forwardRef<HTMLDivElement, ContactNumberProps>((props, ref) => {
  const {
    id,
    name,
    label,
    helpMessage,
    allowNonNumeric = false,
    hasError,
    errorMessage,
    disabled,
    className,
    defaultValue = '',
    value: controlledValue,
    onChange,
  } = props;

  const [value, handleValueChange] = useOptionallyControlledValue<string | undefined>(
    () => defaultValue,
    controlledValue,
    onChange
  );

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    const isValid = allowNonNumeric || !/\D/.test(next);

    if (isValid) {
      handleValueChange(next);
    }
  };

  return (
    <StyledFormGroup
      id={id}
      label={label}
      helpMessage={helpMessage}
      hasError={hasError}
      errorMessage={errorMessage}
      className={className}
      disabled={disabled}
    >
      <Input inputRef={ref} name={name} disabled={disabled} value={value} onChange={handleNumberChange} />
    </StyledFormGroup>
  );
});
